import { useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { Form, Input, InputNumber } from "antd";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import { CreateIOTDeviceSuccessResponse, IOTDevice } from "../../types/api/iot-device/create-iot-device-success-response";
import { showLoadingAlertMessage } from "../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { networkCall } from "../../../../utils/network-call/network-call";
import { showDefaultErrorMessages } from "../../../../utils/show-default-error-message/show-default-error-messages";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../redux/reducer/alert-message.reducer";
import { AlertMessages } from "../../../../enums/alert-messages.enum";
import { DefaultErrorResponse } from "../../../../types/api/error/default-error-response/default-error-respose.type";
import { ValidationErrorResponseData } from "../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { tranformAPIValidationErrorToAntDValidationMessage } from "../../../../utils/validation/transform-api-validation-errors-to-antd-validation-messsages";
import { getImageUrl } from "../../../../utils/image-helper/get-image-url";
import { CMSButton } from "../../../../components/cms/button/cms-button.";

export type IOTDeviceModalType = 'newIOTDevice' | 'editIOTDevice';

type IOTDeviceModalProps = {
    isModalOpen: boolean;
    type: IOTDeviceModalType;
    iotDevice?: IOTDevice;
    onModalToggle: () => void;
    workInstructionId?: number;
    onNewIOTDeviceAdd?: (id: number) => void;
    onIOTDeviceEdit?: () => void;
    onIOTDeviceDelete?: () => void;
};

export const IOTDeviceModal = ({ isModalOpen, type, iotDevice, onModalToggle, workInstructionId, onNewIOTDeviceAdd, onIOTDeviceEdit, onIOTDeviceDelete }: IOTDeviceModalProps) => {
    const [form] = Form.useForm();
    const [inputSuffix, setInputSuffix] = useState('');
    const dispatch = useDispatch();
    const [prevIOTDeviceId, setPrevIOTDeviceId] = useState<number | null>(null);

    if (type === 'editIOTDevice' && iotDevice) {
        if (prevIOTDeviceId !== iotDevice.id) {
            setPrevIOTDeviceId(iotDevice.id);
            form.setFields([{
                name: 'name',
                value: iotDevice.name
            }, {
                name: 'endpoint',
                value: iotDevice.endpoint
            }, {
                name: 'unit',
                value: iotDevice.unit
            }, {
                name: 'lowerLimit',
                value: iotDevice.lowerLimit
            }, {
                name: 'upperLimit',
                value: iotDevice.upperLimit
            }]);
        }
    }

    const handleFinish = async (values: any) => {
        showLoadingAlertMessage();

        if (type === 'newIOTDevice') {
            const response = await networkCall(`/work-instructions/${ workInstructionId }/iot-devices`, {
                method: 'POST',
                body: {
                    name: values.name,
                    endpoint: values.endpoint,
                    unit: values.unit,
                    lowerLimit: values.lowerLimit,
                    upperLimit: values.upperLimit
                }
            });

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if (response?.status === 201) {
                const res: AxiosResponse<CreateIOTDeviceSuccessResponse> = response;

                alertMessageState.alertMessageText = 'Mentés sikeres.';
                alertMessageState.alertMessageType = 'success';
                if (onNewIOTDeviceAdd) onNewIOTDeviceAdd(res.data.iotDevice.id);
                
            } else if (response?.status === 400) {
                const res: AxiosResponse<DefaultErrorResponse> = response;

                switch (res.data.code) {

                    case 1000:
                        const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                        alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                        const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                        form.setFields(validationErrors);
                        break;

                }

            } else if (response?.status === 404) {
                const res: AxiosResponse<DefaultErrorResponse> = response;

                switch (res.data.code) {
                    case 6000:
                        alertMessageState.alertMessageText = 'A munkautasítás nem található, amihez a lépés kívánja hozzáadni.';
                        break;
                }

            }

            dispatch(showAlertMessage(alertMessageState));

        } else {
            
            const response = await networkCall(`/iot-devices/${ iotDevice?.id }`, {
                method: 'PUT',
                body: {
                    name: values.name,
                    endpoint: values.endpoint,
                    unit: values.unit,
                    lowerLimit: values.lowerLimit,
                    upperLimit: values.upperLimit
                }
            });

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if (response?.status === 200) {

                alertMessageState.alertMessageText = 'Mentés sikeres.';
                alertMessageState.alertMessageType = 'success';
                if (onIOTDeviceEdit) onIOTDeviceEdit();
                
            } else if (response?.status === 400) {
                const res: AxiosResponse<DefaultErrorResponse> = response;

                switch (res.data.code) {

                    case 1000:
                        const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                        alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                        const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                        form.setFields(validationErrors);
                        break;

                }

            } else if (response?.status === 404) {
                const res: AxiosResponse<DefaultErrorResponse> = response;

                switch (res.data.code) {
                    case 15000:
                        alertMessageState.alertMessageText = 'Az iOT eszköz nem található.';
                        break;
                }

            }

            dispatch(showAlertMessage(alertMessageState));

        }
    }

    const handleDelete = async () => {
        if (type !== 'editIOTDevice' || !iotDevice) return;

        if (window.confirm('Biztosan törlöd a iOT eszközt?')) {
            showLoadingAlertMessage();

            const response = await networkCall(`/iot-devices/${ iotDevice.id }`, {
                method: 'DELETE'
            });;

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            if(response?.status === 204) {
                if (onIOTDeviceDelete) onIOTDeviceDelete();
                dispatch(showAlertMessage({
                    alertMessageType: 'success',
                    alertMessageText: 'Törlés sikeres.'
                }));
                return;
            }

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response?.status === 404) {
                alertMessageState.alertMessageText = 'Törlés sikertelen, a törlendő iOT eszköz nem létezik.';
            }

            dispatch(showAlertMessage(alertMessageState));
        }
    };

    return (
        <Modal isOpen={isModalOpen} toggle={onModalToggle} scrollable>
            <div className="text-end">
                <img
                    style={{ width: '50px', padding: '10px 10px 0 0' }}
                    src={getImageUrl('close-button.png')}
                    alt="close-button"
                    title="Bezárás"
                    className="cursor-pointer"
                    onClick={onModalToggle}
                />
            </div>
            <ModalHeader className="justify-content-center blue-font pt-0">
                <h3>iOT eszköz {type === 'newIOTDevice' ? 'hozzáadása' : 'módosítása'}</h3>
            </ModalHeader>
            <ModalBody>
                <Form
                    form={form}
                    onFinish={handleFinish}
                    layout='vertical'
                >

                    <Form.Item
                        label={<b className='pink-font'>Név</b>}
                        name='name'
                        rules={[{required: true, message: 'Név megadása kötelező!'}]}
                        className='mb-2'
                    >
                        <Input className='box-shadow-4-4-8-0 border-0' placeholder="Név megadása" />
                    </Form.Item>

                    <Form.Item
                        label={<b className='pink-font'>Végpont</b>}
                        name='endpoint'
                        rules={[{required: true, message: 'Végpont megadása kötelező!'}]}
                        className='mb-2'
                    >
                        <Input className='box-shadow-4-4-8-0 border-0' placeholder="https://www.gmail.com" />
                    </Form.Item>

                    <Form.Item
                        label={<b className='pink-font'>Mértékegység</b>}
                        name='unit'
                        rules={[{required: true, message: 'Mértékegység megadása kötelező!'}]}
                        className='mb-2'
                    >
                        <Input
                            className='box-shadow-4-4-8-0 border-0'
                            placeholder="mértékegység"
                            onChange={(event) => setInputSuffix(event.target.value)}
                        />
                    </Form.Item>

                    <div className="d-flex gap-4">
                        <Form.Item
                            label={<b className='pink-font'>Alsó határérték</b>}
                            name='lowerLimit'
                            rules={[{required: true, message: 'Alsó határérték megadása kötelező!'}]}
                            className='mb-2 flex-grow-1'
                        >
                            <InputNumber
                                className='box-shadow-4-4-8-0 border-0'
                                placeholder="10"
                                style={{ width: '100%' }}
                                suffix={inputSuffix}
                                controls={false}
                                onKeyDown={event => {
                                    if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight','0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', ','].includes(event.key))
                                        event.preventDefault();
                                }}
                                step={0}
                            />
                        </Form.Item>
                        <Form.Item
                            label={<b className='pink-font'>Felső határérték</b>}
                            name='upperLimit'
                            rules={[{required: true, message: 'Felső határérték megadása kötelező!'}]}
                            className='mb-2 flex-grow-1'
                        >
                            <InputNumber
                                className='box-shadow-4-4-8-0 border-0'
                                placeholder="20"
                                style={{ width: '100%' }}
                                suffix={inputSuffix}
                                controls={false}
                                onKeyDown={event => {
                                    if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight','0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', ','].includes(event.key))
                                        event.preventDefault();
                                }}
                                step={0}
                            />
                        </Form.Item>
                    </div>
                    
                    <Form.Item>
                        <div className='mt-4 d-flex justify-content-end gap-3'>
                            {
                                type === 'editIOTDevice' && iotDevice && (
                                    <CMSButton text='Törlés' iconSrc={getImageUrl('delete-icon.png')} type="white" onClick={handleDelete} />
                                )
                            }
                            <CMSButton text='Mentés' iconSrc={getImageUrl('Save-icon.png')} htmlType="submit" />
                        </div>
                    </Form.Item>

                </Form>
            </ModalBody>
        </Modal>
    );
};